import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, FormField, Modal, TextField } from '../../components';
import { LoadingState } from '../../components/feedback/LoadingState';
import { CenteredPage } from '../../components/layout/CenteredPage';
import { isApiError } from '../../lib/api/errors';
import { MFA_REQUIRED_EVENT } from '../../app/queryClient';
import { putDeviceNow } from '../../services/deviceService';
import { PhoneDialog } from '../account/ContactDialogs';
import { mfaKeys, useMfaStatus, useSendMfaCode, useVerifyMfaCode } from './queries';

/**
 * Admin second factor, in front of the whole shell. A profile with no phone
 * gets the blocking "Phone number required for MFA" dialog — its code also
 * verifies this device; otherwise a device that has not passed an SMS code gets
 * the blocking "New Device" dialog. The BE
 * enforces the same rule on every /admin call (MFA_REQUIRED) — if it refuses
 * one later, the gate re-checks and blocks again.
 */
export function MfaGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const status = useMfaStatus(true);
  const recheck = () => void qc.invalidateQueries({ queryKey: mfaKeys.status });

  useEffect(() => {
    const onRequired = () => void qc.invalidateQueries({ queryKey: mfaKeys.status });
    window.addEventListener(MFA_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(MFA_REQUIRED_EVENT, onRequired);
  }, [qc]);

  if (status.isPending) return <LoadingState label="Checking this device…" fill />;
  if (status.isError)
    return (
      <CenteredPage
        title="We couldn't check this device"
        actions={
          <Button fullWidth onClick={() => void status.refetch()}>
            Try again
          </Button>
        }
      >
        The server didn't answer. Check your connection and try again.
      </CenteredPage>
    );
  if (!status.data.phoneOnFile) return <RequiredPhone onDone={recheck} />;
  if (!status.data.deviceVerified) return <NewDeviceDialog onVerified={recheck} />;
  return <>{children}</>;
}

/**
 * The first phone. Confirming its code also verifies this device (the BE reads
 * X-Device-Id on the phone change), so the device row is registered first —
 * then the New Device step, and a second text, is skipped.
 */
function RequiredPhone({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    void putDeviceNow();
  }, []);
  return <PhoneDialog required onClose={onDone} />;
}

function NewDeviceDialog({ onVerified }: { onVerified: () => void }) {
  const send = useSendMfaCode();
  const verify = useVerifyMfaCode();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const sent = useRef(false);

  const sendCode = async () => {
    setError('');
    try {
      // The device row must exist for this user before it can be verified.
      await putDeviceNow();
      await send.mutateAsync();
    } catch {
      setError('The code could not be sent. Try again.');
    }
  };

  // Send once on open (a ref, so a re-render or StrictMode's double mount doesn't text twice).
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    void sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    setError('');
    try {
      await verify.mutateAsync(code);
      onVerified();
    } catch (err) {
      setCode('');
      setError(
        isApiError(err) && err.status === 410
          ? 'That code has expired. Send a new one.'
          : 'That code is not valid. Try again.',
      );
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => undefined}
      hideCloseButton
      size="md"
      title="New Device"
      footer={
        <>
          <Button variant="ghost-dark" size="sm" loading={send.isPending} onClick={() => void sendCode()}>
            Send a new code
          </Button>
          <Button size="sm" type="submit" form="mfa-form" loading={verify.isPending} disabled={code.length < 6}>
            Verify
          </Button>
        </>
      }
    >
      <form id="mfa-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <p className="m-0 text-sm text-muted">
          We detected this as a new device and sent an access code to your phone number on file.
        </p>
        <FormField label="Access code" htmlFor="mfa-code" error={error || undefined}>
          <TextField
            id="mfa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            invalid={!!error}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </FormField>
      </form>
    </Modal>
  );
}
